import React from 'react';
import { Link } from 'react-router-dom';
import {
  Plane,
  Hotel,
  MapPin,
  CalendarClock,
  Car,
  Users,
  Luggage,
  CreditCard,
  Shield,
  Radar,
  MessageCircle,
  Phone,
  Calculator,
  ArrowRight,
  Anchor,
  CheckCircle
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { contactInfo } from '../mock/fareData';

/*
 * 2026-09-06 — Stage 4 SEO landing page: /lpia-to-atlantis
 * Targets: LPIA to Atlantis transportation, Nassau airport to Paradise Island,
 * private transfer to Atlantis. Reuses the /nassau-airport-transfers visual
 * system (cyan/blue gradient, rounded cards, cta panel). Informational only:
 *   - No fare changes, no booking logic changes
 *   - No new backend routes
 *   - No modifications to notifications, payment processing, or FAQ data
 * All CTAs point to existing routes: /book, /fare-calculator, /services,
 * /nassau-airport-transfers, WhatsApp, tel:.
 * Note: does NOT claim Atlantis affiliation / endorsement / partnership.
 */

const highlights = [
  {
    icon: CalendarClock,
    title: 'Pre-Booked LPIA Pickup',
    desc: 'Reserve your Taxi-98 transportation before you arrive in The Bahamas. Share your travel date, arrival time and Paradise Island destination and your pickup will be scheduled in advance.'
  },
  {
    icon: Hotel,
    title: 'Transportation to Atlantis Paradise Island',
    desc: 'Taxi-98 provides pre-booked transportation from Lynden Pindling International Airport (LPIA) to Atlantis Paradise Island, including drop-off at The Royal, The Coral, The Cove and The Reef.'
  },
  {
    icon: Radar,
    title: 'Flight Monitoring',
    desc: 'When you provide flight information with your reservation, Taxi-98 can monitor the arriving flight so we are aware of schedule changes that may affect your planned pickup.'
  },
  {
    icon: Car,
    title: 'Private Scheduled Transportation',
    desc: 'Taxi-98 provides private scheduled transportation to Atlantis — not shared transport. Your reservation is dedicated to your travel party from LPIA to Paradise Island.'
  },
  {
    icon: MapPin,
    title: 'Paradise Island Coverage',
    desc: 'Beyond your LPIA arrival, Taxi-98 also handles scheduled Paradise Island transportation so you can plan the rest of your stay around the same reliable service.'
  },
  {
    icon: Luggage,
    title: 'Luggage Assistance',
    desc: 'Normal luggage assistance is provided at pickup. Bring your suitcases, carry-ons and travel bags — we will help you get everything loaded safely for the ride to Atlantis.'
  }
];

const paradiseServices = [
  'Return transportation',
  'Resort pickups',
  'Dinner transportation',
  'Private scheduled rides',
  'Transportation back to LPIA'
];

const audience = [
  'Solo travelers',
  'Couples',
  'Families',
  'Small groups',
  'Business travelers',
  'Returning visitors'
];

const trustPoints = [
  'Pre-booking for airport arrivals',
  'Scheduled transportation you can plan around',
  'Flight monitoring when flight information is provided',
  'Clear, direct customer communication',
  'Private transportation for your travel party',
  'Professional Bahamian drivers',
  'Paradise Island coverage',
  'Airport transportation from LPIA',
  'Return transportation from Paradise Island'
];

const LpiaToAtlantis = () => {
  const siteUrl = 'https://taxi-98.com';
  const canonicalPath = '/lpia-to-atlantis';

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'LPIA to Atlantis Transportation — Private Airport Transfer to Paradise Island',
    serviceType: 'Airport transfer',
    provider: {
      '@type': 'TaxiService',
      name: 'Taxi-98',
      url: siteUrl,
      areaServed: [
        { '@type': 'Place', name: 'Nassau' },
        { '@type': 'Place', name: 'New Providence' },
        { '@type': 'Place', name: 'Paradise Island' }
      ]
    },
    areaServed: [
      { '@type': 'Place', name: 'Lynden Pindling International Airport (LPIA)' },
      { '@type': 'Place', name: 'Atlantis Paradise Island' },
      { '@type': 'Place', name: 'Paradise Island, Bahamas' }
    ],
    url: `${siteUrl}${canonicalPath}`,
    description:
      'Pre-booked private airport transportation from Lynden Pindling International Airport (LPIA) to Atlantis on Paradise Island, with flight monitoring available when flight information is provided at reservation. Taxi-98 is an independent transportation provider and is not affiliated with, endorsed by, or an official partner of Atlantis.'
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl + '/' },
      { '@type': 'ListItem', position: 2, name: 'Nassau Airport Transfers', item: siteUrl + '/nassau-airport-transfers' },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'LPIA to Atlantis',
        item: siteUrl + canonicalPath
      }
    ]
  };

  return (
    <>
      <SEOHead
        title="LPIA to Atlantis Transportation | Nassau Airport Transfer | Taxi-98"
        description="Pre-book private LPIA to Atlantis transportation with Taxi-98. Scheduled Nassau airport transfers from Lynden Pindling International Airport (LPIA) to Atlantis on Paradise Island."
        canonical={canonicalPath}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <main
        data-testid="lpia-atlantis-page"
        className="relative min-h-screen bg-gradient-to-br from-cyan-50 via-white to-blue-50 pt-28 pb-20 sm:pt-32 sm:pb-28"
      >
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-400/5 rounded-full blur-3xl pointer-events-none"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero */}
          <div className="max-w-3xl mx-auto text-center mb-14 sm:mb-16">
            <span
              className="inline-block bg-cyan-500/10 border border-cyan-400/30 text-cyan-700 text-xs font-bold tracking-[0.22em] uppercase px-4 py-1.5 rounded-full mb-5"
              data-testid="lpia-atlantis-eyebrow"
            >
              LPIA · Paradise Island · Atlantis
            </span>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-5"
              data-testid="lpia-atlantis-headline"
            >
              LPIA to Atlantis Transportation — Private Airport Transfer to Paradise Island
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed">
              Taxi-98 provides pre-booked, scheduled private transportation from
              Lynden Pindling International Airport (LPIA) to Atlantis on
              Paradise Island. Reserve in advance and travel on your own
              schedule.
            </p>

            <div
              className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4"
              data-testid="lpia-atlantis-hero-ctas"
            >
              <Link
                to="/book"
                data-testid="lpia-atlantis-cta-book"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-600 to-blue-700 hover:from-cyan-700 hover:to-blue-800 text-white px-6 py-3 rounded-lg font-semibold text-base transition-all duration-300 shadow-md"
              >
                <span>Book Your Atlantis Transfer</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/fare-calculator"
                data-testid="lpia-atlantis-cta-fare"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white border border-cyan-300 text-cyan-700 hover:bg-cyan-50 px-6 py-3 rounded-lg font-semibold text-base transition-all duration-300"
              >
                <Calculator className="w-4 h-4" />
                <span>Check Your Fare</span>
              </Link>
            </div>

            <p
              className="mt-5 text-sm text-gray-500 flex items-center justify-center gap-2 flex-wrap"
              data-testid="lpia-atlantis-payments-compact"
            >
              <CheckCircle className="w-4 h-4 text-cyan-600" />
              <span>
                Pre-Booking Available • Major Credit Cards • Apple Pay / Tap-to-Pay • PayPal • Cash Accepted
              </span>
            </p>
          </div>

          {/* Highlights grid */}
          <section aria-labelledby="lpia-atlantis-highlights-heading" className="mb-16">
            <h2 id="lpia-atlantis-highlights-heading" className="sr-only">
              LPIA to Atlantis transportation highlights
            </h2>
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6"
              data-testid="lpia-atlantis-highlights"
            >
              {highlights.map(({ icon: Icon, title, desc }) => (
                <div
                  key={title}
                  data-testid={`lpia-atlantis-highlight-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                  className="group bg-white border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-lg hover:border-cyan-300 transition-all duration-300"
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-cyan-500/10 text-cyan-600 mb-4 group-hover:bg-cyan-500/15 transition-colors">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Atlantis towers detail card */}
          <section
            aria-labelledby="lpia-atlantis-towers-heading"
            className="mb-16 bg-white border border-gray-200 rounded-3xl p-8 sm:p-10 shadow-sm"
            data-testid="lpia-atlantis-towers-section"
          >
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 text-cyan-700 font-semibold uppercase text-xs tracking-widest mb-3">
                <Hotel className="w-4 h-4" />
                Atlantis Drop-off
              </div>
              <h2
                id="lpia-atlantis-towers-heading"
                className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4"
              >
                Airport drop-off across Atlantis Paradise Island
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Share your resort details when you book and Taxi-98 will
                schedule your LPIA drop-off across the Atlantis Paradise Island
                property.
              </p>
            </div>
            <ul
              className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3"
              data-testid="lpia-atlantis-towers-list"
            >
              {['Atlantis Paradise Island', 'The Royal', 'The Coral', 'The Cove', 'The Reef'].map((t) => (
                <li key={t} className="flex items-start gap-2 text-gray-700">
                  <CheckCircle className="w-4 h-4 mt-1 text-cyan-600 shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <p
              className="mt-6 text-xs text-gray-500 italic"
              data-testid="lpia-atlantis-independence-disclaimer"
            >
              Taxi-98 is an independent transportation provider and is not
              affiliated with, endorsed by, approved by or officially partnered
              with Atlantis.
            </p>
          </section>

          {/* Paradise Island Transportation + Who We Serve */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6 mb-16">
            <section
              aria-labelledby="lpia-atlantis-paradise-heading"
              className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 shadow-sm"
              data-testid="lpia-atlantis-paradise-section"
            >
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-cyan-500/10 text-cyan-600 mb-4">
                <MapPin className="w-6 h-6" />
              </div>
              <h2
                id="lpia-atlantis-paradise-heading"
                className="text-xl sm:text-2xl font-bold text-gray-900 mb-3"
              >
                Paradise Island Transportation
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Taxi-98 also provides scheduled transportation around Paradise
                Island beyond your initial airport transfer. Common requests
                include:
              </p>
              <ul
                className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-gray-700 text-sm"
                data-testid="lpia-atlantis-paradise-list"
              >
                {paradiseServices.map((s) => (
                  <li key={s} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 text-cyan-600 shrink-0" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section
              aria-labelledby="lpia-atlantis-audience-heading"
              className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 shadow-sm"
              data-testid="lpia-atlantis-audience-section"
            >
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-cyan-500/10 text-cyan-600 mb-4">
                <Users className="w-6 h-6" />
              </div>
              <h2
                id="lpia-atlantis-audience-heading"
                className="text-xl sm:text-2xl font-bold text-gray-900 mb-3"
              >
                Who We Serve
              </h2>
              <ul
                className="grid grid-cols-2 gap-x-4 gap-y-2 text-gray-700 text-sm"
                data-testid="lpia-atlantis-audience-list"
              >
                {audience.map((a) => (
                  <li key={a} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 text-cyan-600 shrink-0" />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          {/* Payment Options */}
          <section
            aria-labelledby="lpia-atlantis-payments-heading"
            className="mb-16 bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 shadow-sm"
            data-testid="lpia-atlantis-payments-section"
          >
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-cyan-500/10 text-cyan-600 mb-4">
              <CreditCard className="w-6 h-6" />
            </div>
            <h2
              id="lpia-atlantis-payments-heading"
              className="text-xl sm:text-2xl font-bold text-gray-900 mb-3"
            >
              Payment Options
            </h2>
            <p className="text-gray-700 mb-2 font-semibold">
              Pre-Booking Available • Major Credit Cards • Apple Pay / Tap-to-Pay • PayPal • Cash Accepted
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              Visa, Mastercard, American Express, Discover, Apple Pay /
              Tap-to-Pay, PayPal and Cash. Payment can be completed at pickup or
              confirmed after booking.
            </p>
          </section>

          {/* Trust positioning */}
          <section
            aria-labelledby="lpia-atlantis-trust-heading"
            className="mb-16"
            data-testid="lpia-atlantis-trust-section"
          >
            <div className="max-w-3xl mb-6">
              <h2
                id="lpia-atlantis-trust-heading"
                className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3"
              >
                What travelers rely on Taxi-98 for
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Every LPIA to Atlantis reservation is handled the same way:
                scheduled, private and pre-planned. Here is what we focus on.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {trustPoints.map((item) => (
                <div
                  key={item}
                  className="flex items-start gap-3 bg-white border border-gray-200 rounded-xl p-4"
                >
                  <Shield className="w-5 h-5 mt-0.5 text-cyan-600 shrink-0" />
                  <p className="text-gray-700 text-sm">{item}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Internal links */}
          <section
            aria-labelledby="lpia-atlantis-related-heading"
            className="mb-16"
            data-testid="lpia-atlantis-related-links"
          >
            <h2
              id="lpia-atlantis-related-heading"
              className="text-xl sm:text-2xl font-bold text-gray-900 mb-4"
            >
              Plan the rest of your Nassau ride
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <Link
                to="/book"
                data-testid="lpia-atlantis-related-book"
                className="group bg-white border border-gray-200 hover:border-cyan-300 hover:shadow-md rounded-xl p-5 transition-all"
              >
                <Plane className="w-5 h-5 text-cyan-600 mb-2" />
                <p className="font-semibold text-gray-900 mb-1">Book a ride</p>
                <p className="text-sm text-gray-600">Reserve your Atlantis transfer.</p>
              </Link>
              <Link
                to="/fare-calculator"
                data-testid="lpia-atlantis-related-fare"
                className="group bg-white border border-gray-200 hover:border-cyan-300 hover:shadow-md rounded-xl p-5 transition-all"
              >
                <Calculator className="w-5 h-5 text-cyan-600 mb-2" />
                <p className="font-semibold text-gray-900 mb-1">Fare Calculator</p>
                <p className="text-sm text-gray-600">Estimate your LPIA fare.</p>
              </Link>
              <Link
                to="/nassau-airport-transfers"
                data-testid="lpia-atlantis-related-airport"
                className="group bg-white border border-gray-200 hover:border-cyan-300 hover:shadow-md rounded-xl p-5 transition-all"
              >
                <MapPin className="w-5 h-5 text-cyan-600 mb-2" />
                <p className="font-semibold text-gray-900 mb-1">Nassau Airport Transfers</p>
                <p className="text-sm text-gray-600">All LPIA transportation.</p>
              </Link>
              <Link
                to="/services"
                data-testid="lpia-atlantis-related-services"
                className="group bg-white border border-gray-200 hover:border-cyan-300 hover:shadow-md rounded-xl p-5 transition-all"
              >
                <Car className="w-5 h-5 text-cyan-600 mb-2" />
                <p className="font-semibold text-gray-900 mb-1">All Services</p>
                <p className="text-sm text-gray-600">Every Taxi-98 service.</p>
              </Link>
              <Link
                to="/#faq"
                data-testid="lpia-atlantis-related-faq"
                className="group bg-white border border-gray-200 hover:border-cyan-300 hover:shadow-md rounded-xl p-5 transition-all"
              >
                <MessageCircle className="w-5 h-5 text-cyan-600 mb-2" />
                <p className="font-semibold text-gray-900 mb-1">FAQs</p>
                <p className="text-sm text-gray-600">Common traveler questions.</p>
              </Link>
              <Link
                to="/lpia-to-baha-mar"
                data-testid="lpia-atlantis-related-bahamar"
                className="group bg-white border border-gray-200 hover:border-cyan-300 hover:shadow-md rounded-xl p-5 transition-all"
              >
                <Hotel className="w-5 h-5 text-cyan-600 mb-2" />
                <p className="font-semibold text-gray-900 mb-1">LPIA to Baha Mar Transportation</p>
                <p className="text-sm text-gray-600">Cable Beach resort transfers.</p>
              </Link>
              <Link
                to="/cruise-port-transportation"
                data-testid="lpia-atlantis-related-cruise"
                className="group bg-white border border-gray-200 hover:border-cyan-300 hover:shadow-md rounded-xl p-5 transition-all"
              >
                <Anchor className="w-5 h-5 text-cyan-600 mb-2" />
                <p className="font-semibold text-gray-900 mb-1">Nassau Cruise Port Transportation</p>
                <p className="text-sm text-gray-600">Cruise-port pickup and drop-off.</p>
              </Link>
            </div>
          </section>

          {/* CTA panel */}
          <div
            className="bg-gradient-to-br from-cyan-600 to-blue-700 rounded-3xl p-8 sm:p-10 lg:p-12 text-white shadow-xl"
            data-testid="lpia-atlantis-cta-panel"
          >
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3">
                Ready to reserve your LPIA to Atlantis transfer?
              </h2>
              <p className="text-base sm:text-lg text-cyan-50 mb-8">
                Pre-book online, estimate your fare in seconds, or reach a real
                person on WhatsApp or by phone. We are happy to help with
                anything special you need.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
                <Link
                  to="/book"
                  data-testid="lpia-atlantis-cta-book-panel"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-cyan-700 hover:bg-cyan-50 px-6 py-3 rounded-lg font-semibold text-base transition-all duration-300 shadow-md"
                >
                  <span>Book Your Atlantis Transfer</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>

                <Link
                  to="/fare-calculator"
                  data-testid="lpia-atlantis-cta-fare-panel"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-cyan-700/40 border border-white/30 hover:bg-cyan-700/60 text-white px-6 py-3 rounded-lg font-semibold text-base transition-all duration-300 backdrop-blur-sm"
                >
                  <Calculator className="w-4 h-4" />
                  <span>Check Your Fare</span>
                </Link>

                <a
                  href={`https://wa.me/${contactInfo.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(
                    'Hello Taxi-98, I would like to pre-book a Nassau airport transfer to Atlantis.'
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="lpia-atlantis-cta-whatsapp"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-semibold text-base transition-all duration-300 shadow-md"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>WhatsApp Us</span>
                </a>
              </div>

              <div className="mt-7 pt-7 border-t border-white/20 text-sm sm:text-base text-cyan-50">
                <p className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3">
                  <span className="inline-flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    <a
                      href={`tel:${contactInfo.phone.replace(/[^+\d]/g, '')}`}
                      data-testid="lpia-atlantis-cta-phone"
                      className="font-semibold hover:underline"
                    >
                      {contactInfo.phone}
                    </a>
                  </span>
                  <span className="hidden sm:inline opacity-50">•</span>
                  <span className="opacity-90">{contactInfo.hours}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default LpiaToAtlantis;
